import { useState } from "react";
import {
  OPEN_STAGES,
  PROSPECT_SOURCES,
  PROSPECT_STAGES,
  emptyProspect,
  isFollowUpDue,
  todayISO,
} from "../../api/prospects";
import { Field, PanelMessage, SelectField, Spinner, formatDate } from "./shared";
import { BackIcon, ChevronIcon } from "./icons";
import styles from "./Admin.module.css";
import ProspectCsvImport from "./ProspectCsvImport";

/*
  Sales: businesses the team is approaching. Each prospect moves through
  stages (to contact → contacted → follow up → interested → won / not
  interested). A won prospect can be turned into a client in one click.
*/
export default function SalesPanel({ prospects, onConvert }) {
  const [editing, setEditing] = useState(null); // null | "new" | prospect object
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  if (prospects.error && !prospects.items && !showImport) {
    return (
      <PanelMessage>
        {prospects.error}{" "}
        <button className={styles.linkBtn} onClick={prospects.load}>Retry</button>
      </PanelMessage>
    );
  }
  if (prospects.items === null && !showImport) return <PanelMessage><Spinner /></PanelMessage>;

  const items = prospects.items || [];
  const today = todayISO();
  const q = search.trim().toLowerCase();
  const visible = items
    .filter((p) => showClosed || OPEN_STAGES.includes(p.stage || "new"))
    .filter((p) =>
      !q ||
      [p.businessName, p.contactName, p.phone, p.email, p.location, p.service]
        .some((v) => String(v || "").toLowerCase().includes(q))
    )
    .sort((a, b) => Number(isFollowUpDue(b, today)) - Number(isFollowUpDue(a, today)));

  const closedCount = items.filter(
    (p) => !OPEN_STAGES.includes(p.stage || "new")
  ).length;

  const selected = selectedId != null
    ? items.find((p) => p.id === selectedId)
    : null;

  const removePropspect = (p) => {
    if (!window.confirm(`Delete prospect "${p.businessName || p.contactName}"? This cannot be undone.`)) return;
    prospects.remove(p.id);
    setSelectedId(null);
  };

  // ── Detail view ──────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className={styles.dash} key="detail">
        <button
          className={styles.backBtn}
          onClick={() => { setSelectedId(null); setEditing(null); }}
        >
          <BackIcon /> All prospects
        </button>

        {editing && editing !== "new" ? (
          <ProspectForm
            prospect={editing}
            onCancel={() => setEditing(null)}
            onSave={async (data) => {
              await prospects.update(editing.id, data);
              setEditing(null);
            }}
          />
        ) : (
          <ProspectDetail
            prospect={selected}
            today={today}
            onEdit={() => setEditing(selected)}
            onDelete={() => removePropspect(selected)}
            onConvert={() => onConvert(selected)}
            onStage={(stage) => prospects.setStage(selected.id, stage)}
          />
        )}

        {prospects.error && <p className={styles.error}>{prospects.error}</p>}
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className={styles.dash} key="list">
      {showImport && (
        <ProspectCsvImport
          onImport={prospects.importCsv}
          onBusyChange={setImportBusy}
          ready={prospects.items !== null}
          onClose={() => setShowImport(false)}
          onImported={() => { setSearch(""); setShowClosed(true); }}
        />
      )}
      {editing && (
        <ProspectForm
          prospect={editing === "new" ? emptyProspect : editing}
          onCancel={() => setEditing(null)}
          onSave={async (data) => {
            if (editing === "new") await prospects.add(data);
            else await prospects.update(editing.id, data);
            setEditing(null);
          }}
        />
      )}

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search business, contact, phone, area or service"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search prospects"
        />
        <div className={styles.headActions}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
            />
            Show won / not interested{closedCount ? ` (${closedCount})` : ""}
          </label>
          <button className={styles.ghostBtn} onClick={prospects.load} disabled={showImport}>Refresh</button>
          <button className={styles.ghostBtn} onClick={() => { setEditing(null); setShowImport(true); }}>Import CSV</button>
          <button className={styles.solidBtn} disabled={showImport} onClick={() => setEditing("new")}>
            + Add prospect
          </button>
        </div>
      </div>

      {prospects.error && <p className={styles.error}>{prospects.error}</p>}

      {visible.length === 0 ? (
        <PanelMessage>
          {items.length === 0
            ? "No prospects yet. Add the businesses you plan to approach."
            : "No prospects match."}
        </PanelMessage>
      ) : (
        <div className={`${styles.tableWrap} ${styles.stackWrap}`}>
          <table className={`${styles.table} ${styles.stackTable}`}>
            <thead>
              <tr>
                <th>Business</th>
                <th>Contact</th>
                <th>Pitch</th>
                <th>Follow-up</th>
                <th>Stage</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const open = OPEN_STAGES.includes(p.stage || "new");
                const due = isFollowUpDue(p, today);
                const overdue = due && p.nextFollowUp < today;
                const openDetail = () => { if (!importBusy) setSelectedId(p.id); };
                return (
                  <tr
                    key={p.id}
                    className={`${styles.rowLink} ${open ? "" : styles.rowClosed}`}
                    onClick={openDetail}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); } }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open prospect ${p.businessName || p.contactName}`}
                  >
                    {/* Business name */}
                    <td className={styles.cellTitle}>
                      <strong>{p.businessName || p.contactName || "—"}</strong>
                      {p.businessName && p.contactName && (
                        <div className={styles.dim}>{p.contactName}</div>
                      )}
                      {p.location && <div className={styles.dim}>{p.location}</div>}
                    </td>

                    {/* Contact links */}
                    <td
                      className={`${styles.contactCell} ${styles.cellSub} ${styles.cellOptional}`}
                      data-label="Contact"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {p.phone && (
                        <a href={`tel:${p.phone.replace(/\s+/g, "")}`}>{p.phone}</a>
                      )}
                      {p.whatsapp && (
                        <a
                          href={`https://wa.me/${p.whatsapp.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          WhatsApp
                        </a>
                      )}
                      {p.email && <a href={`mailto:${p.email}`}>{p.email}</a>}
                    </td>

                    {/* Pitch / service */}
                    <td
                      className={`${styles.msgCell} ${styles.cellSub} ${styles.cellOptional}`}
                      data-label="Pitch"
                    >
                      {p.service && <div>{p.service}</div>}
                      {p.source && <div className={styles.dim}>via {p.source}</div>}
                    </td>

                    {/* Follow-up date */}
                    <td
                      className={`${styles.cellWide} ${styles.cellOptional}`}
                      data-label="Follow-up"
                    >
                      {p.nextFollowUp ? (
                        <span className={styles.followUpCell}>
                          {formatDate(p.nextFollowUp)}
                          {due && (
                            <span
                              className={`${styles.pill} ${overdue ? styles.access_off : styles.status_new}`}
                            >
                              {overdue ? "Overdue" : "Due today"}
                            </span>
                          )}
                        </span>
                      ) : null}
                    </td>

                    {/* Stage select — stops row click so it stays interactive */}
                    <td
                      className={styles.cellStatus}
                      data-label="Stage"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <select
                        className={`${styles.select} ${styles[`stage_${p.stage}`] || ""}`}
                        value={p.stage || "new"}
                        disabled={importBusy}
                        onChange={(e) => prospects.setStage(p.id, e.target.value)}
                        aria-label={`Stage for ${p.businessName || p.contactName}`}
                      >
                        {PROSPECT_STAGES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>

                    <td className={styles.rowChevron} aria-hidden="true"><ChevronIcon /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className={styles.dim}>
        Stages: {PROSPECT_STAGES.map((s) => s.label).join(" → ")}. Tap a row to see
        details, edit or delete.
      </p>
    </div>
  );
}

// ── Prospect detail card ──────────────────────────────────────────────────────
function ProspectDetail({ prospect: p, today, onEdit, onDelete, onConvert, onStage }) {
  const due = isFollowUpDue(p, today);
  const overdue = due && p.nextFollowUp < today;
  const stageLabel = PROSPECT_STAGES.find((s) => s.value === (p.stage || "new"))?.label || p.stage;

  return (
    <section className={styles.card}>
      <div className={styles.detailHead}>
        <div>
          <span className={styles.badge}>Prospect</span>
          <h2 className={styles.detailTitle}>{p.businessName || p.contactName || "—"}</h2>
          {p.businessName && p.contactName && (
            <p className={styles.sub}>{p.contactName}</p>
          )}
        </div>
        <div className={styles.detailActions}>
          <select
            className={`${styles.select} ${styles[`stage_${p.stage}`] || ""}`}
            value={p.stage || "new"}
            onChange={(e) => onStage(e.target.value)}
            aria-label="Stage"
          >
            {PROSPECT_STAGES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {!p.clientId && (
            <button className={styles.solidBtn} onClick={onConvert}>Make client</button>
          )}
          {p.clientId && (
            <span className={`${styles.pill} ${styles.access_on}`}>Client</span>
          )}
          <button className={styles.ghostBtn} onClick={onEdit}>Edit</button>
          <button className={styles.dangerBtn} onClick={onDelete}>Delete</button>
        </div>
      </div>

      <div className={styles.detailGrid}>
        {/* Contact block */}
        <div className={styles.detailBlock}>
          <h3>Contact</h3>
          {p.location && <p className={styles.dim}>{p.location}</p>}
          <dl className={`${styles.kv} ${styles.kvFlush}`}>
            {p.phone && (
              <div>
                <dt>Phone</dt>
                <dd><a href={`tel:${p.phone.replace(/\s+/g, "")}`}>{p.phone}</a></dd>
              </div>
            )}
            {p.whatsapp && (
              <div>
                <dt>WhatsApp</dt>
                <dd>
                  <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                    {p.whatsapp}
                  </a>
                </dd>
              </div>
            )}
            {p.email && (
              <div>
                <dt>Email</dt>
                <dd><a href={`mailto:${p.email}`}>{p.email}</a></dd>
              </div>
            )}
            {!p.phone && !p.whatsapp && !p.email && (
              <div><dt>No contact info saved</dt><dd /></div>
            )}
          </dl>
        </div>

        {/* Pipeline block */}
        <div className={styles.detailBlock}>
          <h3>Pipeline</h3>
          <dl className={`${styles.kv} ${styles.kvFlush}`}>
            <div><dt>Stage</dt><dd>{stageLabel}</dd></div>
            {p.source && <div><dt>Found via</dt><dd>{p.source}</dd></div>}
            {p.service && <div><dt>Pitching</dt><dd>{p.service}</dd></div>}
            {p.nextFollowUp && (
              <div>
                <dt>Next follow-up</dt>
                <dd>
                  <span className={styles.followUpCell}>
                    {formatDate(p.nextFollowUp)}
                    {due && (
                      <span className={`${styles.pill} ${overdue ? styles.access_off : styles.status_new}`}>
                        {overdue ? "Overdue" : "Due today"}
                      </span>
                    )}
                  </span>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {p.notes && (
        <div className={styles.detailBlock}>
          <h3>Notes</h3>
          <p className={styles.detailNotes}>{p.notes}</p>
        </div>
      )}
    </section>
  );
}

// ── Prospect form ─────────────────────────────────────────────────────────────
function ProspectForm({ prospect, onSave, onCancel }) {
  const [form, setForm] = useState({
    ...emptyProspect,
    ...prospect,
    nextFollowUp: prospect.nextFollowUp || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.businessName.trim() && !form.contactName.trim()) {
      setError("Enter at least a business name or a contact name.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.message || "Could not save the prospect.");
      setBusy(false);
    }
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2>{prospect.id ? "Edit prospect" : "New prospect"}</h2>
      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <Field label="Business name" value={form.businessName} onChange={set("businessName")} />
        <Field label="Contact person" value={form.contactName} onChange={set("contactName")} />
        <Field label="Phone" value={form.phone} onChange={set("phone")} />
        <Field label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} />
        <Field label="Location / area" value={form.location} onChange={set("location")} />
        <SelectField label="Found via" value={form.source} onChange={set("source")}>
          <option value="">—</option>
          {PROSPECT_SOURCES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </SelectField>
        <Field
          label="What to pitch"
          value={form.service}
          onChange={set("service")}
          hint="e.g. Google Business Profile, website, Instagram setup"
        />
        <SelectField label="Stage" value={form.stage} onChange={set("stage")}>
          {PROSPECT_STAGES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </SelectField>
        <Field label="Next follow-up" type="date" value={form.nextFollowUp} onChange={set("nextFollowUp")} />
      </fieldset>
      <fieldset className={styles.group} disabled={busy}>
        <Field
          label="Notes"
          value={form.notes}
          onChange={set("notes")}
          textarea
          hint="Who you spoke to, what they said, objections, best time to call."
        />
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save prospect"}
        </button>
        <button className={styles.ghostBtn} type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
